import JSZip from "jszip";

const PNG_SIZES = [
    { fileName: "app-icon-1024.png", size: 1024, label: "App Store Master — App Icon" },
    { fileName: "logo512.png", size: 512, label: "React Web App — logo512.png" },
    { fileName: "logo192.png", size: 192, label: "React Web App — logo192.png" },
    { fileName: "apple-touch-icon.png", size: 180, label: "iOS Safari Web — apple-touch-icon.png" },
];

const README = `Jigsaw Muse app icon pack
Generated from 1024×1024 master PNG.

Files:
- app-icon-1024.png (1024×1024) — App Store Master, opaque PNG
- favicon.ico (16×16 & 32×32) — React Web App favicon
- logo192.png (192×192) — React Web App
- logo512.png (512×512) — React Web App
- apple-touch-icon.png (180×180) — iOS Safari Web
`;

async function blobToUint8Array(blob) {
    return new Uint8Array(await blob.arrayBuffer());
}

async function loadImageBitmap(imageUrl) {
    const response = await fetch(imageUrl);
    if (!response.ok) {
        throw new Error("Failed to load image");
    }
    const blob = await response.blob();
    return createImageBitmap(blob);
}

function resizeSquareToPngBlob(imageBitmap, size) {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            reject(new Error("Canvas not supported"));
            return;
        }
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(imageBitmap, 0, 0, size, size);
        canvas.toBlob(
            (blob) => {
                if (blob) resolve(blob);
                else reject(new Error("PNG encode failed"));
            },
            "image/png",
        );
    });
}

/** Vista+ ICO: embed PNG payloads (works in modern browsers). */
function encodeIcoFromPngEntries(entries) {
    const pngArrays = entries.map((entry) => entry.png);
    const count = pngArrays.length;
    const headerSize = 6;
    const dirEntrySize = 16;
    const dataOffsetStart = headerSize + dirEntrySize * count;
    const totalSize = dataOffsetStart + pngArrays.reduce((sum, png) => sum + png.length, 0);

    const buffer = new ArrayBuffer(totalSize);
    const view = new DataView(buffer);
    const bytes = new Uint8Array(buffer);

    view.setUint16(0, 0, true);
    view.setUint16(2, 1, true);
    view.setUint16(4, count, true);

    let dataOffset = dataOffsetStart;
    entries.forEach((entry, index) => {
        const png = entry.png;
        const entryOffset = headerSize + index * dirEntrySize;
        const dimensionByte = entry.size >= 256 ? 0 : entry.size;

        bytes[entryOffset] = dimensionByte;
        bytes[entryOffset + 1] = dimensionByte;
        bytes[entryOffset + 2] = 0;
        bytes[entryOffset + 3] = 0;
        view.setUint16(entryOffset + 4, 1, true);
        view.setUint16(entryOffset + 6, 32, true);
        view.setUint32(entryOffset + 8, png.length, true);
        view.setUint32(entryOffset + 12, dataOffset, true);

        bytes.set(png, dataOffset);
        dataOffset += png.length;
    });

    return buffer;
}

function triggerBlobDownload(blob, fileName) {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
}

/**
 * Builds a ZIP of web + store icon sizes from a square PNG source URL.
 * @param {string} imageUrl
 * @returns {Promise<Blob>}
 */
export async function buildJigsawIconPackZip(imageUrl) {
    const bitmap = await loadImageBitmap(imageUrl);
    try {
        const zip = new JSZip();

        for (const entry of PNG_SIZES) {
            const pngBlob = await resizeSquareToPngBlob(bitmap, entry.size);
            zip.file(entry.fileName, pngBlob);
        }

        const png16Blob = await resizeSquareToPngBlob(bitmap, 16);
        const png32Blob = await resizeSquareToPngBlob(bitmap, 32);
        const png16 = await blobToUint8Array(png16Blob);
        const png32 = await blobToUint8Array(png32Blob);
        const icoBuffer = encodeIcoFromPngEntries([
            { size: 16, png: png16 },
            { size: 32, png: png32 },
        ]);
        zip.file("favicon.ico", icoBuffer);
        zip.file("README.txt", README);

        return zip.generateAsync({ type: "blob" });
    } finally {
        bitmap.close?.();
    }
}

/**
 * @param {string} imageUrl
 * @param {string} [zipFileName]
 */
export async function downloadJigsawIconPack(imageUrl, zipFileName = "jigsaw-app-icon-pack.zip") {
    const zipBlob = await buildJigsawIconPackZip(imageUrl);
    triggerBlobDownload(zipBlob, zipFileName);
}

export { PNG_SIZES };
